// aifab by aiiop.ai - App.jsx
import { useState, useEffect, useCallback } from 'react';
import './styles/global.css';
import { apiFetch } from './hooks/apiFetch';

import LandingPage  from './pages/LandingPage';
import DocsPage     from './pages/DocsPage';
import LoginPage    from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import Dashboard    from './pages/Dashboard';
import ProjectsPage from './pages/ProjectsPage';
import DataPage     from './pages/DataPage';
import ServersPage  from './pages/ServersPage';
import ModelsPage   from './pages/ModelsPage';
import PluginsPage  from './pages/PluginsPage';
import RunsPage     from './pages/RunsPage';
import LineagePage    from './pages/LineagePage';
import CreateRunPage  from './pages/CreateRunPage';
import ModelCatalog  from './pages/ModelCatalog';
import AdminPage     from './pages/AdminPage';

import { Sidebar } from './components/layout/Sidebar';
import { Topbar }  from './components/layout/Topbar';
import TourGuide    from './components/tour/TourGuide';
import { useApi }   from './hooks/useApi';

const PROJECT_KEY = 'aifab_project_id';

// ── Auth helpers ──────────────────────────────────────────────────────────────
function loadStoredUser() {
  // FE-M2: the token is an httpOnly cookie JS can't read, so auth state is
  // derived from the cached profile. Expiry is enforced server-side: any API
  // call past expiry returns 401, which apiFetch turns into an aifab:logout.
  const raw = localStorage.getItem('aifab_user');
  if (!raw) { clearAuth(); return null; }

  try {
    return JSON.parse(raw);
  } catch (_) {
    clearAuth();
    return null;
  }
}

function clearAuth() {
  localStorage.removeItem('aifab_user');
  localStorage.removeItem(PROJECT_KEY);
  sessionStorage.removeItem('aifab_page');
  // FE-L1: per-run package markers (aifab_pkg_*) are dynamic; purge them all so
  // they don't survive logout and leak across sessions on a shared machine.
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith('aifab_pkg_'))
      .forEach(k => localStorage.removeItem(k));
  } catch (_e) { /* localStorage unavailable - nothing to purge */ }
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [user,       setUser]       = useState(loadStoredUser);
  const { data: appInfoData } = useApi('/app-info');
  const appVersion = appInfoData?.version || '2.0';

  const [page,       setPage]       = useState(() => sessionStorage.getItem('aifab_page') || 'dashboard');
  const [tourActive,  setTourActive]  = useState(false);
  const [projectId,  setProjectId]  = useState(() => localStorage.getItem(PROJECT_KEY) || '');
  // Show landing only when no valid session exists.
  // If the user has a valid token (hard refresh, new tab while logged in) - go straight to app.
  const [showLand,   setShowLand]   = useState(() => !loadStoredUser());
  const [showDocs,   setShowDocs]   = useState(false);
  // Hoist projects fetch here so Sidebar and ProjectsPage share one request
  const { data: projects, reload: reloadProjects } = useApi(user ? '/projects' : null);
  const [showLogin,  setShowLogin]  = useState(false);
  // Reason shown on the login screen when redirected due to expiry
  const [loginMsg,   setLoginMsg]   = useState(null);

  // ── Session expiry handler ─────────────────────────────────────────────────
  const handleLogout = useCallback((event) => {
    const reason = event?.detail?.reason;
    clearAuth();
    setUser(null);
    setProjectId('');
    setPage('dashboard');
    setShowLand(false);
    setShowLogin(true); // go to login on logout/expiry, not landing
    if (reason === 'session_expired') {
      setLoginMsg('Your session has expired. Please sign in again.');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('aifab:logout', handleLogout);
    return () => window.removeEventListener('aifab:logout', handleLogout);
  }, [handleLogout]);

  // ── Periodic expiry check while tab is open ────────────────────────────────
  // Catches the case where the user leaves the tab open past token expiry
  // without making any API call. FE-M2: the token is httpOnly so we can't parse
  // it client-side; instead ping /me, which 401s once the cookie expires and
  // apiFetch converts that into an aifab:logout.
  useEffect(() => {
    if (!user) return;
    const check = () => {
      apiFetch('/auth/me').catch(() => { /* 401 already fires aifab:logout */ });
    };
    // Check every 5 min - light touch, the real trigger is the 401 path.
    const t = setInterval(check, 300_000);
    return () => clearInterval(t);
  }, [user, handleLogout]);

  // ── Project context ────────────────────────────────────────────────────────
  const changeProject = (id) => {
    setProjectId(id);
    if (id) localStorage.setItem(PROJECT_KEY, id);
    else    localStorage.removeItem(PROJECT_KEY);
  };

  // ── Login ──────────────────────────────────────────────────────────────────
  const onLogin = (profile) => {
    if (profile) localStorage.setItem('aifab_user', JSON.stringify(profile));
    setUser(profile);
    setShowLand(false);
    setShowLogin(false);
    setLoginMsg(null);
  };

  const onGetStarted = () => {
    const stored = loadStoredUser();
    if (stored) {
      setUser(stored);
      setShowLand(false);
      setShowLogin(false);
    } else {
      setShowLand(false);
      setShowLogin(true);
    }
  };

  const manualLogout = () => {
    // FE-M2: tell the server to clear the httpOnly cookies and revoke sessions.
    // Best-effort - local state is cleared regardless.
    apiFetch('/auth/logout', { method: 'POST' }).catch(() => {});
    handleLogout({});
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (showDocs) {
    return <DocsPage onBack={(view, section) => { setShowDocs(false); if (view) setTimeout(() => { const el = section ? document.getElementById(section) : null; if (el) el.scrollIntoView({behavior:'smooth'}); }, 100); }} onGetStarted={onGetStarted} />;
  }
  if (showLand) {
    return <LandingPage onGetStarted={onGetStarted} onGoToDocs={() => setShowDocs(true)} />;
  }
  if (!user) {
    return <LoginPage onLogin={onLogin} sessionMessage={loginMsg} />;
  }
  // Forced first-login / admin-required password change. The change-password
  // call revokes all sessions, so on success we drop the token and send the
  // user back to login with a confirmation message.
  if (user.must_change_password) {
    return (
      <ChangePasswordPage
        email={user.email}
        onChanged={() => {
          localStorage.removeItem('aifab_user');
          setUser(null);
          setShowLogin(true);
          setLoginMsg('Password changed. Please sign in with your new password.');
        }}
      />
    );
  }

  const navigateTo = (p) => { sessionStorage.setItem('aifab_page', p); setPage(p); setTourActive(false); };

  const pageProps = { projectId, onNavigate: navigateTo };

  return (
    <div className="app">
      <Sidebar
        projects={projects}
        reloadProjects={reloadProjects}
        page={page}
        onNavigate={navigateTo}
        user={user}
        onLogout={manualLogout}
        projectId={projectId}
        onProjectChange={changeProject}
      />
      <div className="main">
        <Topbar page={page} version={appVersion} onTour={() => setTourActive(true)} />
        <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
          {page === 'dashboard' && <Dashboard    {...pageProps} />}
          {page === 'projects'  && <ProjectsPage {...pageProps} projects={projects} reloadProjects={reloadProjects} />}
          {page === 'data'      && <DataPage     {...pageProps} />}
          {page === 'models'    && <ModelsPage   {...pageProps} />}
          {page === 'catalog'   && <ModelCatalog {...pageProps} />}
          {page === 'admin'     && <AdminPage    {...pageProps} user={user} />}
          {page === 'lineage'   && <LineagePage projectId={projectId} onNavigate={setPage} user={user} />}
          {page === 'runs'      && <RunsPage     {...pageProps}
              onCreateRun={() => setPage('create-run')} />}
          {page === 'create-run' && <CreateRunPage
              projectId={projectId}
              onBack={() => setPage('runs')}
              onSaved={() => setPage('runs')} />}
          {page === 'servers'   && <ServersPage  {...pageProps} />}
          {page === 'plugins'   && <PluginsPage  {...pageProps} />}
        </div>
      </div>
      {tourActive && <TourGuide page={page} onClose={() => setTourActive(false)} />}
    </div>
  );
}
